import type {
  ShopOverviewTab,
  ShopOverviewTimeRange,
  ShopOverviewTrendPoint,
} from '../api';

export type OverviewTabOption = {
  key: ShopOverviewTab;
  label: string;
};

export type OverviewTimeRangeOption = {
  key: ShopOverviewTimeRange;
  label: string;
};

export interface NumericTrendPoint {
  x: string;
  y: number;
}

export const SHOP_OVERVIEW_TAB_OPTIONS: OverviewTabOption[] = [
  { key: 'trade', label: '交易概况' },
  { key: 'settlement', label: '结算数据' },
  { key: 'refund', label: '退款数据' },
  { key: 'traffic', label: '流量数据' },
];

export const SHOP_OVERVIEW_TIME_RANGE_OPTIONS: OverviewTimeRangeOption[] = [
  { key: 'today', label: '今天' },
  { key: '1d', label: '近1天' },
  { key: '7d', label: '近7天' },
  { key: '30d', label: '近30天' },
];

export const SHOWCASE_OVERVIEW_TIME_RANGE_OPTIONS: OverviewTimeRangeOption[] = [
  { key: 'today', label: '今天' },
  { key: '7d', label: '近7天' },
  { key: '30d', label: '近30天' },
  { key: 'natural_week', label: '自然周' },
];

function formatDateTimeAtDayStart(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}/${month}/${day} 00:00:00`;
}

export function buildShowcaseNaturalWeekTimeParams(value?: Date | null) {
  if (!value) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekEnd = new Date(yesterday);
    const day = weekEnd.getDay();
    const daysToSunday = day === 0 ? 0 : day;
    weekEnd.setDate(weekEnd.getDate() - daysToSunday);
    const weekBegin = new Date(weekEnd);
    weekBegin.setDate(weekBegin.getDate() - 6);
    return {
      beginDate: formatDateTimeAtDayStart(weekBegin),
      endDate: formatDateTimeAtDayStart(weekEnd),
      dateType: 3,
    };
  }

  const selectedDate = new Date(value);
  selectedDate.setHours(0, 0, 0, 0);
  const day = selectedDate.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const weekBegin = new Date(selectedDate);
  weekBegin.setDate(weekBegin.getDate() + mondayOffset);
  const weekEnd = new Date(weekBegin);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const latestSelectable = new Date();
  latestSelectable.setHours(0, 0, 0, 0);
  latestSelectable.setDate(latestSelectable.getDate() - 1);
  if (weekEnd > latestSelectable) {
    weekEnd.setTime(latestSelectable.getTime());
  }
  return {
    beginDate: formatDateTimeAtDayStart(weekBegin),
    endDate: formatDateTimeAtDayStart(weekEnd),
    dateType: 3,
  };
}

export function buildShopOverviewTimeParams(timeRange: ShopOverviewTimeRange) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (timeRange === 'today') {
    return {
      beginDate: formatDateTimeAtDayStart(today),
      endDate: formatDateTimeAtDayStart(today),
      dateType: 1,
    };
  }

  if (timeRange === '1d') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      beginDate: formatDateTimeAtDayStart(yesterday),
      endDate: formatDateTimeAtDayStart(yesterday),
      dateType: 20,
    };
  }

  if (timeRange === '7d') {
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);
    const beginDate = new Date(endDate);
    beginDate.setDate(beginDate.getDate() - 6);
    return {
      beginDate: formatDateTimeAtDayStart(beginDate),
      endDate: formatDateTimeAtDayStart(endDate),
      dateType: 21,
    };
  }

  if (timeRange === 'natural_week') {
    return buildShowcaseNaturalWeekTimeParams();
  }

  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() - 1);
  const beginDate = new Date(endDate);
  beginDate.setDate(beginDate.getDate() - 29);
  return {
    beginDate: formatDateTimeAtDayStart(beginDate),
    endDate: formatDateTimeAtDayStart(endDate),
    dateType: 23,
  };
}

export function normalizeBusinessType(value?: string | null) {
  return String(value || '').trim().toUpperCase();
}

export function isSelfOperatedBusinessType(value?: string | null) {
  return normalizeBusinessType(value) === 'SELF_OPERATED';
}

export function isBuyingBusinessType(value?: string | null) {
  return normalizeBusinessType(value) === 'BUYING';
}

export function normalizeMetricName(value?: string | null) {
  return String(value || '').trim();
}

export function toNumericTrendPoints(points: ShopOverviewTrendPoint[] | undefined) {
  return (points || [])
    .map((point) => {
      const numericValue = typeof point.y === 'number' && Number.isFinite(point.y) ? point.y : null;
      if (numericValue === null) {
        return null;
      }
      return {
        x: point.x || '',
        y: numericValue,
      };
    })
    .filter((item): item is NumericTrendPoint => Boolean(item));
}

export function buildVisibleTrendPointIndexSet(total: number, maxVisiblePoints = 8) {
  const visibleIndexes = new Set<number>();
  if (total <= 0) {
    return visibleIndexes;
  }

  if (total <= maxVisiblePoints) {
    for (let index = 0; index < total; index += 1) {
      visibleIndexes.add(index);
    }
    return visibleIndexes;
  }

  const step = Math.max(1, Math.ceil((total - 1) / Math.max(1, maxVisiblePoints - 1)));
  for (let index = 0; index < total; index += step) {
    visibleIndexes.add(index);
  }
  visibleIndexes.add(0);
  visibleIndexes.add(total - 1);

  return visibleIndexes;
}
